import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Crown, MessageCircle, Users, Sparkles, RefreshCw } from 'lucide-react'
import { useSubscriptionStore } from '../stores/subscriptionStore'
import SubscriptionPlans from './SubscriptionPlans'

interface UsageGuardProps {
  children: React.ReactNode
  feature: 'chat' | 'compatibility'
  onUsageIncrement?: () => void
}

const UsageGuard: React.FC<UsageGuardProps> = ({ 
  children, 
  feature,
  onUsageIncrement 
}) => {
  const { 
    hasFreeChatCallsRemaining, 
    hasFreeCompatibilityCallsRemaining,
    getRemainingChatCalls,
    getRemainingCompatibilityCalls,
    incrementChatCalls,
    incrementCompatibilityCalls,
    isSubscriptionActive,
    resetUsageStats
  } = useSubscriptionStore()
  
  const [showPlans, setShowPlans] = useState(false)
  // const [hasShownUpgradePrompt, setHasShownUpgradePrompt] = useState(false)

  const hasRemaining = feature === 'chat' 
    ? hasFreeChatCallsRemaining() 
    : hasFreeCompatibilityCallsRemaining()
  
  const remainingCalls = feature === 'chat' 
    ? getRemainingChatCalls() 
    : getRemainingCompatibilityCalls()

  const handleFeatureUse = () => {
    if (isSubscriptionActive()) {
      // Subscribed users have unlimited access
      onUsageIncrement?.()
      return
    }

    if (hasRemaining) {
      // Increment usage and allow feature
      if (feature === 'chat') {
        incrementChatCalls()
      } else {
        incrementCompatibilityCalls()
      }
      onUsageIncrement?.()
    } else {
      // Show upgrade prompt
      setShowPlans(true)
              // setHasShownUpgradePrompt(true)
    }
  }

  // If user has active subscription, show content normally
  if (isSubscriptionActive()) {
    return <>{children}</>
  }

  // If user has remaining calls, show content with usage tracking
  if (hasRemaining) {
    return (
      <div onClick={handleFeatureUse} className="cursor-pointer">
        {children}
        {/* Usage indicator */}
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm shadow-lg z-50">
          <div className="flex items-center gap-2">
            {feature === 'chat' ? (
              <MessageCircle className="w-4 h-4" />
            ) : (
              <Users className="w-4 h-4" />
            )}
            <span>
              {remainingCalls} free {feature === 'chat' ? 'chats' : 'compatibility checks'} remaining
            </span>
            {/* Development reset button */}
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  resetUsageStats()
                }}
                className="ml-2 p-1 hover:bg-gray-700 rounded"
                title="Reset usage (dev only)"
              >
                <RefreshCw className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // If user has exceeded free limit, show upgrade prompt
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative">
        {/* Blurred Content */}
        <div className="blur-sm pointer-events-none">
          {children}
        </div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-2xl flex items-center justify-center">
          <div className="text-center p-8 max-w-md">
            <div className="mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                {feature === 'chat' ? (
                  <MessageCircle className="w-8 h-8 text-white" />
                ) : (
                  <Users className="w-8 h-8 text-white" />
                )}
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Free {feature === 'chat' ? 'Chat' : 'Compatibility'} Limit Reached
              </h3>
              <p className="text-gray-300 text-sm">
                You've used all {feature === 'chat' ? '20 free chat calls' : '20 free compatibility checks'}. 
                Upgrade to continue using this feature unlimited times.
              </p>
            </div>

            <motion.button 
              onClick={() => setShowPlans(true)}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 mx-auto"
            >
              <Crown className="w-5 h-5" />
              Upgrade Now
            </motion.button>

            <div className="mt-4 text-xs text-gray-400">
              <div className="flex items-center justify-center gap-1">
                <Sparkles className="w-3 h-3" />
                <span>Unlimited access with any subscription plan</span>
              </div>
            </div>

            {/* Development reset button */}
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={() => resetUsageStats()}
                className="mt-4 text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1 mx-auto"
                title="Reset usage (dev only)"
              >
                <RefreshCw className="w-3 h-3" />
                Reset Usage (Dev)
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Subscription Plans Modal */}
      {showPlans && (
        <SubscriptionPlans 
          onClose={() => setShowPlans(false)}
          showUpgradePrompt={true}
        />
      )}
    </>
  )
}

export default UsageGuard 